import { FC } from 'react';
import {
    CaseloadReading,
    IAgencyDigest,
    IHistoryProgress,
    IInsightsFilter,
    describeCaseload,
    describeHistoryRule
} from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { CaseloadReadingFilters } from './CaseloadReadingFilters';
import { InsightsFilterBar } from './InsightsFilterBar';
import { HoneycombCaseloadMap, IHoneycombCell } from './HoneycombCaseloadMap';
import { InsightsCard } from './InsightsCard';
import { InsightsInfoTip } from './InsightsInfoTip';
import { BeaSays } from './BeaSays';
import { CaseloadUpdated } from './CaseloadUpdated';
import { NeedsHistory } from './NeedsHistory';

/**
 * The caseload at a glance: one cell per client, in the caseload's own order rather than worst-first,
 * and Bea's sentence about it.
 *
 * The honeycomb is the one surface where a client keeps the same position from visit to visit, which
 * is what lets someone find a person on it. Re-sorting it by severity would move everybody every
 * time anything changed.
 *
 * The cells are the digest's own readings, which fold a client with less raised than usual into
 * steady; Bea's sentence under them may still say such a client is better than usual, and the Outcomes
 * Report counts them. The colour key above the map counts each reading and, on screen, narrows the
 * map to it. The rest of the page stays the whole caseload's, except that Needs Attention lists every
 * incident while the map is narrowed to them. It stands even while the map waits on history, since
 * an incident needs none.
 */
export const CaseloadSummaryReport: FC<{
    digest: IAgencyDigest;
    history: IHistoryProgress;
    /** When the map is narrowed, the clients still in scope. Everyone else stays but recedes. */
    matchingIds?: string[];
    /** What the map is narrowed to, for the bar that says so. Absent on paper. */
    filter?: IInsightsFilter;
    /** The reading the map is narrowed to, when it is. */
    readingNarrowed?: CaseloadReading;
    /** Whether the map is narrowed at all. */
    narrowed?: boolean;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    /** The largest a map cell is drawn, where the page has a set amount of room. */
    cellRadius?: number;
    onOpenClient: (careMapId: string) => void;
    /** Absent on paper, where the key only reads. */
    onToggleReading?: (reading: CaseloadReading) => void;
    onClearFilter?: () => void;
    /** Asks agency Bea about her line under the map, where she is offered. */
    onAskBea?: (line: string) => void;
}> = ({ digest, history, matchingIds, filter, readingNarrowed, narrowed = false, clientAvatar, cellRadius, onOpenClient, onToggleReading, onClearFilter, onAskBea }) => {
    const cells: IHoneycombCell[] = digest.baselines.map(baseline => ({
        careMapId: baseline.careMapId,
        clientName: baseline.clientName,
        reading: baseline.reading,
        identity: clientAvatar(baseline.careMapId)
    }));
    const counts: { [key in CaseloadReading]: number } = { incident: 0, worthWatching: 0, steady: 0, unknown: 0 };
    for (const baseline of digest.baselines) {
        counts[baseline.reading]++;
    }
    const caseloadLine = describeCaseload(digest);

    return (
        <InsightsCard
            heading="Caseload Summary"
            action={(
                <InsightsInfoTip label="About the caseload summary" summary="One hexagon per client, coloured by their reading: an incident, more raised than their own usual, steady, or not enough on record to compare. Press a reading in the key to pick out those clients on the map.">
                    {!history.hasEnough && describeHistoryRule(history)}
                </InsightsInfoTip>
            )}
        >
            <CaseloadReadingFilters
                counts={counts}
                clientCount={digest.clientCount}
                hasHistory={history.hasEnough}
                filter={readingNarrowed}
                narrowed={narrowed}
                onToggleReading={onToggleReading}
                onClear={onClearFilter}
                aside={<CaseloadUpdated digest={digest} />}
            />

            <NeedsHistory history={history} panel="caseload">
                {narrowed && filter && onClearFilter && (
                    <InsightsFilterBar filter={filter} clientCount={matchingIds?.length ?? 0} onClear={onClearFilter} />
                )}

                <HoneycombCaseloadMap
                    cells={cells}
                    matchingIds={matchingIds}
                    cellRadius={cellRadius}
                    onSelectClient={onOpenClient}
                />

                <BeaSays panel="caseload" onAsk={onAskBea && (() => onAskBea(caseloadLine))}>{caseloadLine}</BeaSays>
            </NeedsHistory>
        </InsightsCard>
    );
};
